import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const CookiePolicyPage = () => {
  return (
    <div data-testid="cookie-policy-page" className="pt-20 min-h-screen bg-slate-950">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-b from-slate-900 to-slate-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/" 
            className="inline-flex items-center text-blue-400 hover:text-blue-300 mb-8"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Link>
          <h1 
            className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4"
            style={{ fontFamily: 'var(--font-heading)' }}
          >
            Cookie Policy
          </h1>
          <p className="text-slate-400">Last updated: February 2026</p>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-invert prose-slate max-w-none">
            
            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">1. What Are Cookies?</h2>
              <p className="text-slate-300 leading-relaxed">
                Cookies are small text files that are stored on your computer or mobile device 
                when you visit a website. They are widely used to make websites work more 
                efficiently and to provide information to website owners.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">2. How We Use Cookies</h2>
              <p className="text-slate-300 leading-relaxed">
                Nassau Engineering uses cookies to improve your experience on our website. 
                We use both essential cookies (necessary for the website to function) and 
                non-essential cookies (for analytics and performance).
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">3. Types of Cookies We Use</h2>
              
              <div className="bg-slate-900 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-white mb-3">Essential Cookies</h3>
                <p className="text-slate-300 mb-4">
                  These cookies are necessary for the website to function properly. They cannot be disabled.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 text-slate-400">Cookie</th>
                      <th className="text-left py-2 text-slate-400">Purpose</th>
                      <th className="text-left py-2 text-slate-400">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr className="border-b border-slate-800">
                      <td className="py-2">cookie_consent</td>
                      <td className="py-2">Stores your cookie preferences</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-slate-900 rounded-lg p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Analytics Cookies (Non-Essential)</h3>
                <p className="text-slate-300 mb-4">
                  These cookies help us understand how visitors interact with our website.
                </p>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-2 text-slate-400">Cookie</th>
                      <th className="text-left py-2 text-slate-400">Purpose</th>
                      <th className="text-left py-2 text-slate-400">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-300">
                    <tr className="border-b border-slate-800">
                      <td className="py-2">_ga</td>
                      <td className="py-2">Google Analytics - Distinguishes users</td>
                      <td className="py-2">2 years</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-2">_gid</td>
                      <td className="py-2">Google Analytics - Distinguishes users</td>
                      <td className="py-2">24 hours</td>
                    </tr>
                    <tr className="border-b border-slate-800">
                      <td className="py-2">ph_*</td>
                      <td className="py-2">PostHog - Analytics and session recording</td>
                      <td className="py-2">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">4. Managing Your Cookie Preferences</h2>
              <p className="text-slate-300 leading-relaxed mb-4">
                When you first visit our website, you will see a cookie consent banner that 
                allows you to accept all cookies or reject non-essential cookies.
              </p>
              <p className="text-slate-300 leading-relaxed mb-4">
                You can also manage cookies through your browser settings:
              </p>
              <ul className="list-disc list-inside text-slate-300 space-y-2 ml-4">
                <li><strong className="text-white">Chrome:</strong> Settings → Privacy and Security → Cookies</li>
                <li><strong className="text-white">Firefox:</strong> Settings → Privacy & Security → Cookies</li>
                <li><strong className="text-white">Safari:</strong> Preferences → Privacy → Cookies</li>
                <li><strong className="text-white">Edge:</strong> Settings → Privacy → Cookies</li>
              </ul>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">5. Third-Party Cookies</h2>
              <p className="text-slate-300 leading-relaxed">
                Some cookies are placed by third-party services that appear on our pages. 
                We use Google Analytics and PostHog for website analytics. These services 
                may set their own cookies, which are governed by their respective privacy policies.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">6. Updates to This Policy</h2>
              <p className="text-slate-300 leading-relaxed">
                We may update this Cookie Policy from time to time. Any changes will be posted 
                on this page with an updated revision date.
              </p>
            </div>

            <div className="mb-10">
              <h2 className="text-xl font-semibold text-white mb-4">7. Contact Us</h2>
              <p className="text-slate-300 leading-relaxed">
                If you have questions about our use of cookies, please contact us at:
              </p>
              <div className="mt-4 text-slate-300">
                <p><strong className="text-white">Nassau Engineering</strong></p>
                <p>Unit 4b, Canal Bank, Hume Ave</p>
                <p>Park West Industrial Park</p>
                <p>Dublin, D12 ARX3, Ireland</p>
                <p className="mt-2">Phone: (01) 630 1410</p>
                <p>Email: info@nassauengineering.ie</p>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default CookiePolicyPage;
